import React, { useState, ChangeEvent, FormEvent } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { IoHome } from "react-icons/io5";
import signUpImg from "../assets/img/bg8.jpg";
import logo1 from "../assets/img/rsilogo.png";
import { FaLocationDot } from "react-icons/fa6";
import useScrollToTop from "../components/useScrollToTop";
import { account, database } from "../appWrite";
import { FaEye, FaEyeSlash } from "react-icons/fa";

// Define the shape of the form data
interface FormData {
  firstname: string;
  lastname: string;
  phone: string;
  email: string;
  password: string;
}

const Signup: React.FC = () => {
  useScrollToTop();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false); // State to toggle password visibility

  const [formData, setFormData] = useState<FormData>({
    firstname: "",
    lastname: "",
    phone: "",
    email: "",
    password: "",
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Create user
      const user = await account.create(
        "unique()",
        formData.email,
        formData.password,
        formData.firstname
      );

      // Log in the user using Appwrite
      await account.createEmailPasswordSession(
        formData.email,
        formData.password
      );

      // Store additional attributes in the database
      await database.createDocument(
        "67ce8a4a0032e13c6368", // replace with your actual database ID
        "67ce8a5b002afcaf4f27", // replace with your actual collection ID
        "unique()",
        {
          userId: user.$id,
          phone: formData.phone,
          email: formData.email,
        }
      );

      navigate("/signup-success");
      setIsLoading(false);
    } catch (error: any) {
      const errorMessage = error.message;
      const errorDiv = document.querySelector(".error-div") as HTMLDivElement;

      if (errorMessage) {
        errorDiv.textContent = errorMessage;
        errorDiv.classList.toggle("hidden");
        setTimeout(() => {
          errorDiv.classList.toggle("hidden");
        }, 2000);
      }
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 1 }}
      className="w-full h-screen flex flex-col md:flex-row"
    >
      <div className="hidden md:flex w-full md:w-1/2 relative">
        <img src={logo1} alt="Logo" className="w-16 ml-4 mt-2 absolute" />
        <img
          src={signUpImg}
          className="w-full h-full object-cover"
          alt="bg-img"
        />
        <h3 className="absolute bottom-8 left-4 text-lg font-semibold text-gray-400 flex items-center gap-1">
          Rehoboth
          <FaLocationDot className="animate-pulse" />
        </h3>
      </div>
      <div className="w-full md:w-1/2 lg:pt-10 bg-slate-50 flex flex-col justify-center p-8">
        <div className="flex bg-slate-50 justify-between items-center mb-8 md:mb-4 md:pt-12">
          <h1 className="text-3xl font-semibold">Sign up</h1>
          <Link to="/">
            <IoHome className="text-3xl text-primary hover:text-primary-dark" />
          </Link>
        </div>
        <div className="error-div hidden mb-4 bg-primary text-white w-[76%] px-6 mx-auto py-2 rounded-lg"></div>
        <div className="bg-white p-6 mt-[4rem] rounded-lg shadow-xl">
          <form onSubmit={handleSubmit}>
            <input
              type="text"
              name="firstname"
              id="firstname"
              placeholder="First name"
              value={formData.firstname}
              onChange={handleChange}
              className="border border-gray-400 py-2 px-4 w-full rounded mb-3"
            />
            <input
              type="text"
              name="lastname"
              id="lastname"
              placeholder="Last name"
              required
              value={formData.lastname}
              onChange={handleChange}
              className="border border-gray-400 py-2 px-4 w-full rounded mb-3"
            />
            <input
              type="tel"
              name="phone"
              id="phone"
              placeholder="Phone number"
              required
              value={formData.phone}
              onChange={handleChange}
              className="border border-gray-400 py-2 px-4 w-full rounded mb-3"
            />
            <input
              type="email"
              name="email"
              id="email"
              placeholder="Email address"
              required
              value={formData.email}
              onChange={handleChange}
              className="border border-gray-400 py-2 px-4 w-full rounded mb-3"
            />
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password" // Make sure this matches the key in `formData`
                id="password"
                placeholder="Password"
                required
                value={formData.password}
                onChange={handleChange}
                className="border border-gray-400 py-2 px-4 w-full rounded mb-3 pr-10"
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute top-[1.4rem] right-3 transform -translate-y-1/2 text-gray-500"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            <p className="mt-2 flex gap-2">
              Already have an account?{" "}
              <Link className="font-semibold text-primary" to="/login">
                Log in
              </Link>
            </p>
            <button
              disabled={isLoading}
              className={`w-full py-2 mt-5 bg-primary text-white font-semibold rounded hover:bg-primary-dark"
              type="submit ${isLoading ? "animate-pulse" : ""}`}
            >
              {isLoading ? "Loading..." : "Submit"}
            </button>
          </form>
        </div>
        <h3 className="font-bold mt-8 text-center text-primary md:mt-32">
          Rehoboth
        </h3>
      </div>
    </motion.div>
  );
};

export default Signup;
